/**
 * Runtime (post-build) configuration, fetched from `/config/app-config.json`
 * at app startup. This is what makes the build artifact environment-agnostic:
 * the same compiled bundle is deployed to every environment, and each Static
 * Web App instance serves its own `/config/app-config.json` (see
 * pipeline/templates/deploy-environment.yml) instead of values being baked in
 * by Vite.
 *
 * `loadRuntimeEnv()` must be awaited before any module that reads
 * `getRuntimeEnv()` (constants/config.ts, config/oidcConfig.ts) is imported —
 * see the dynamic-import bootstrap in main.ts and silent-renew.ts.
 */
type RuntimeEnv = ImportMetaEnv;

let cached: RuntimeEnv | null = null;

/** Fetch and cache `/config/app-config.json`, merged over build-time defaults as a fallback. */
export async function loadRuntimeEnv(): Promise<RuntimeEnv> {
  if (cached) return cached;

  try {
    const response = await fetch('/config/app-config.json', { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    cached = { ...import.meta.env, ...(await response.json()) } as RuntimeEnv;
  } catch (error) {
    console.warn(
      '[runtimeEnv] Could not load /config/app-config.json, using build-time defaults:',
      error,
    );
    cached = { ...import.meta.env } as RuntimeEnv;
  }

  return cached;
}

/** Synchronous accessor. Falls back to build-time env if called before `loadRuntimeEnv()` resolves (e.g. in tests). */
export function getRuntimeEnv(): RuntimeEnv {
  return cached ?? ({ ...import.meta.env } as RuntimeEnv);
}
